/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { AddCircle, Clear, Edit, Search } from "@mui/icons-material";
import { Box, Button, Card, CardContent, Grid, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridRowId, GridRowModel, GridSortDirection, GridSortModel } from "@mui/x-data-grid";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Checkbox from "@mui/material/Checkbox";
import useThrowAsyncError from "@/hooks/useThrowAsyncError";

import AutoComplete from "@/components/hookForms/autoComplete";
import { Container } from "@/theme/globalStyles";
import DataTable from "@/components/dataTable";
import { useAuth } from "@/context/global";
import IGenericResult from "@/interfaces/IGenericResult";
import serviceConcessionaria from "@/services/serviceConcessionaria";
import AutoCompleteCheckbox from "@/components/hookForms/autoCompleteCheckbox";
import serviceRebalanceUsina from "@/services/serviceRebalanceUsina";
import DialogNew from "./modal/dialogAdd";
import DialogEdit from "./modal/dialogEdit";
import { statusFilterOptions, statusOptions, tipoRebateFilterOptions } from "@/utils/constants";
import serviceParametrizacaoRebate from "@/services/serviceParametrizacaoRebate";
import InputNumber from "@/components/hookForms/inputNumber";

const ParametrosRebate = () => {
    const { setTitlePage } = useAuth();
    const [concessionariaDataOptions, setConcessionariaDataOptions] = useState<IGenericResult[]>([]);
    const [usinaDataOptions, setUsinaDataOptions] = useState<any[]>([]);
    const [openModalAdd, setOpenModalAdd] = useState<boolean>(false);
    const [data, setData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [filters, setFilters] = useState(null);
    const [page, setPage] = useState(0);
    const [rowCount, setRowCount] = useState(10);
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: "",
            sort: "" as GridSortDirection,
        },
    ]);
    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon fontSize="small" />;

    useEffect(() => {
        setTitlePage("Análise Tarifária - Parâmetros Rebate");
    }, [setTitlePage]);

    const [pageSize, setPageSize] = useState(100);
    const [editState, setEditState] = useState({
        open: false,
        parametrizacaoRebateID: 0,
        versao: "",
        tipoRebate: "",
        tipoCalculo: "",
        classe: "",
        concessionariaId: 0,
        usinaId: "",
        estado: "",
        percentualDesconto: "",
        valorDescontoInicial: "",
        valorDescontoFinal: "",
        ativo: "",
        observacao: "",
        nomeUsina: "",
        nomeConcessionaria: "",
        referenciaRebalance: "",
    });

    const {
        control,
        handleSubmit,
        reset,
        resetField,
        formState: { errors },
        // eslint-disable-next-line react-hooks/rules-of-hooks
    } = useForm({
        defaultValues: {
            parametrizacaoRebateID: "",
            ativo: "Ativo",
            versao: "",
            tipoRebate: "",
            concessionariaId: "",
            usinasIds: [],
        },
    });

    const actionColumns = useMemo(
        () => [
            {
                headerName: "Operações",
                field: "operations",
                type: "actions",
                width: 120,
                hide: false,
                getActions: (params: { id: GridRowId; row: GridRowModel }) => [
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Editar">
                                <Edit />
                            </Tooltip>
                        }
                        label="Editar"
                        onClick={() =>
                            setEditState({
                                open: true,
                                parametrizacaoRebateID: params.row.parametrizacaoRebateID,
                                tipoRebate: params.row.tipoRebate + 1,
                                versao: params.row.versao,
                                tipoCalculo: params.row.tipoCalculo + 1,
                                classe: params.row.classe,
                                concessionariaId: params.row.concessionariaId,
                                usinaId: params.row.usinaId,
                                estado: params.row.estado,
                                percentualDesconto: params.row.percentualDesconto,
                                valorDescontoInicial: params.row.valorDescontoInicial,
                                valorDescontoFinal: params.row.valorDescontoFinal,
                                ativo: params.row.ativo,
                                observacao: params.row.observacao,
                                nomeConcessionaria: params.row.nomeConcessionaria,
                                nomeUsina: params.row.nomeUsina,
                                referenciaRebalance: params.row.referenciaRebalance,
                            })
                        }
                    />,
                ],
            },
        ],
        []
    );

    const columns = useMemo(
        () => [
            {
                headerName: "ID",
                field: "parametrizacaoRebateID",
                type: "number",
                headerAlign: "left",
                identity: true,
                minWidth: 100,
                flex: 1,
                id: 70,
            },
            {
                headerName: "Versão",
                field: "versao",
                type: "string",
                minWidth: 100,
                flex: 1,
            },
            {
                headerName: "Tipo Rebate",
                field: "tipoRebateDescricao",
                type: "string",
                minWidth: 150,
                flex: 1,
            },
            {
                headerName: "Tipo de Cálculo",
                field: "tipoCalculoDescricao",
                type: "string",
                minWidth: 150,
                flex: 1,
            },
            {
                headerName: "Concessionária",
                field: "nomeConcessionaria",
                type: "string",
                minWidth: 300,
                flex: 1,
            },
            {
                headerName: "Usina",
                field: "nomeUsina",
                type: "string",
                minWidth: 200,
                flex: 1,
            },
            {
                headerName: "Tipo Pesquisa Cliente",
                field: "referenciaRebalance",
                type: "string",
                minWidth: 200,
                flex: 1,
            },
            {
                headerName: "Classe",
                field: "classeDescricao",
                type: "string",
                minWidth: 150,
                flex: 1,
            },
            {
                headerName: "Estado",
                field: "estado",
                type: "string",
                minWidth: 100,
                flex: 1,
            },
            {
                headerName: "Desconto - De",
                field: "valorDescontoInicial",
                type: "string",
                minWidth: 150,
                flex: 1,
            },
            {
                headerName: "Desconto - Até",
                field: "valorDescontoFinal",
                type: "string",
                minWidth: 150,
                flex: 1,
            },
            {
                headerName: "Rebate %",
                field: "percentualDesconto",
                type: "string",
                minWidth: 150,
                flex: 1,
            },
            {
                headerName: "Status",
                field: "ativo",
                type: "string",
                minWidth: 100,
                flex: 1,
            },
            {
                headerName: "Observação",
                field: "observacao",
                type: "string",
                minWidth: 500,
                flex: 1,
            },
            ...actionColumns,
        ],
        [actionColumns]
    );

    useEffect(() => {
        getAllWithFilters(filters);
    }, [page, pageSize, sortModel]);

    useEffect(() => {
        setConcessionariaData();
    }, []);

    const throwAsyncError = useThrowAsyncError();

    const getAllWithFilters = async (filters: any) => {
        setData([]);
        setIsLoading(true);

        if (!filters) {
            filters = { ativo: true };
        }

        serviceParametrizacaoRebate
            .getWithFilter({
                ...filters,
                take: pageSize,
                currentPage: page + 1,
                sortExpression: sortModel[0]?.field ?? "",
                sortDirection: sortModel[0]?.sort ?? "",
            })
            .then((result) => {
                if (!result) {
                    throw new Error("Falha ao carregar os dados.");
                }
                setData(result.data.content);
                setRowCount(result.data.numberOfElements);
            })
            .catch((err) => {
                setData([]);
                setRowCount(0);
                throwAsyncError(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const getUsinaData = async (values: any) => {
        if (values.concessionariaId) {
            serviceRebalanceUsina
                .getUsinasByConcessionaria(values)
                .then((result) => {
                    if (result) {
                        setUsinaDataOptions(result?.data);
                    }
                })
                .catch(() => {
                    setUsinaDataOptions([]);
                });
        }
    };

    const setConcessionariaData = async () => {
        serviceConcessionaria
            .getConcessionarias()
            .then((result) => {
                setConcessionariaDataOptions(result?.data);
            })
            .catch(() => {
                setConcessionariaDataOptions([]);
            });
    };

    const onClear = () => {
        reset({ parametrizacaoRebateID: "", versao: "", tipoRebate: "", concessionariaId: "", ativo: "" });
        setFilters(null);
        getAllWithFilters(null);
    };

    const onSuccessAction = () => {
        getAllWithFilters(filters);
    };

    const onSubmit = (values: any) => {
        const newFilters = {
            ...values,
            ativo: statusFilterOptions[values.ativo],
            tipoRebate: typeof values.tipoRebate === "number" ? values.tipoRebate - 1 : "",
        };

        setFilters(newFilters);
        getAllWithFilters(newFilters);
    };

    return (
        <Container>
            <Card>
                <CardContent>
                    <Box sx={{ mb: 2 }}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={2} mt={2}>
                                <Grid item md={12} xs={12}>
                                    <Grid container spacing={2} display="flex" alignItems="center">
                                        <Grid item md={2} xs={12}>
                                            <InputNumber
                                                name="parametrizacaoRebateID"
                                                label="ID"
                                                control={control}
                                                error={errors}
                                            />
                                        </Grid>
                                        <Grid item md={2} xs={12}>
                                            <InputNumber
                                                name="versao"
                                                label="Versão"
                                                control={control}
                                                error={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <AutoComplete
                                                name="tipoRebate"
                                                label="Tipo Rebate"
                                                options={tipoRebateFilterOptions}
                                                control={control}
                                                error={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <AutoComplete
                                                name="ativo"
                                                label="Status"
                                                options={statusOptions}
                                                control={control}
                                                error={errors}
                                            />
                                        </Grid>
                                        <Grid item md={2} xs={12}>
                                            <Button
                                                disableElevation
                                                variant="contained"
                                                color="warning"
                                                fullWidth
                                                style={{ margin: "16px 0px 8px 0px" }}
                                                startIcon={<Search />}
                                                type="submit"
                                            >
                                                Pesquisar
                                            </Button>
                                        </Grid>
                                        <Grid item md={2} xs={12}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                style={{ margin: "16px 0px 8px 0px" }}
                                                startIcon={<AddCircle />}
                                                onClick={() => setOpenModalAdd(true)}
                                                disableElevation
                                                fullWidth
                                                size="medium"
                                            >
                                                Novo
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item md={4} xs={12}>
                                    <AutoComplete
                                        name="concessionariaId"
                                        label="Concessionária"
                                        options={concessionariaDataOptions ?? []}
                                        control={control}
                                        error={errors}
                                        onInputChange={(_, __, reason) => {
                                            if (reason === "clear") {
                                                setUsinaDataOptions([]);
                                                resetField("usinasIds");
                                            }
                                        }}
                                        onChange={(idConcessionaria) => {
                                            setUsinaDataOptions([]);
                                            resetField("usinasIds");
                                            if (idConcessionaria) {
                                                getUsinaData({ concessionariaId: idConcessionaria });
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <AutoCompleteCheckbox
                                        name="usinasIds"
                                        label="Usinas"
                                        options={usinaDataOptions ?? []}
                                        control={control}
                                        error={errors}
                                        limitTags={1}
                                        disableCloseOnSelect={true}
                                        renderOption={(props, option, { selected }) => (
                                            <li {...props}>
                                                <Checkbox
                                                    icon={icon}
                                                    checkedIcon={checkedIcon}
                                                    style={{ marginRight: 8 }}
                                                    checked={selected}
                                                />
                                                {option.label}
                                            </li>
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <Button
                                        style={{ marginTop: 16, marginBottom: 8 }}
                                        variant="outlined"
                                        color="warning"
                                        startIcon={<Clear />}
                                        onClick={() => onClear()}
                                        disableElevation
                                        fullWidth
                                        size="medium"
                                    >
                                        Limpar
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    </Box>
                    <Box sx={{ height: 450 }}>
                        <DataTable
                            columns={columns}
                            rows={data ?? []}
                            rowId={(row: GridRowModel) => row.parametrizacaoRebateID}
                            rowCount={rowCount}
                            isLoading={isLoading}
                            onPageChange={(currentPage: number) => setPage(currentPage)}
                            onPageSizeChange={(currentPageSize: number) => setPageSize(currentPageSize)}
                            pageSize={pageSize}
                            onSortModelChange={(model: GridSortModel) => setSortModel(model)}
                        />
                    </Box>
                </CardContent>
            </Card>
            <DialogNew
                open={openModalAdd}
                concessionariaDataOptions={concessionariaDataOptions}
                onSuccess={() => onSuccessAction()}
                onClose={() => setOpenModalAdd(false)}
            />

            <DialogEdit
                open={editState.open}
                concessionariaDataOptions={concessionariaDataOptions}
                onSuccess={() => onSuccessAction()}
                data={{
                    open: editState.open,
                    parametrizacaoRebateID: editState.parametrizacaoRebateID,
                    tipoRebate: editState.tipoRebate,
                    versao: editState.versao,
                    tipoCalculo: editState.tipoCalculo,
                    classe: editState.classe,
                    concessionariaId: editState.concessionariaId,
                    usinaId: editState.usinaId,
                    estado: editState.estado,
                    percentualDesconto: editState.percentualDesconto,
                    valorDescontoInicial: editState.valorDescontoInicial,
                    valorDescontoFinal: editState.valorDescontoFinal,
                    ativo: editState.ativo,
                    observacao: editState.observacao,
                    nomeUsina: editState.nomeUsina,
                    nomeConcessionaria: editState.nomeConcessionaria,
                    referenciaRebalance: editState.referenciaRebalance,
                }}
                onClose={() =>
                    setEditState({
                        open: false,
                        parametrizacaoRebateID: 0,
                        tipoRebate: "",
                        versao: "",
                        tipoCalculo: "",
                        classe: "",
                        concessionariaId: 0,
                        usinaId: "",
                        estado: "",
                        percentualDesconto: "",
                        valorDescontoInicial: "",
                        valorDescontoFinal: "",
                        ativo: "",
                        observacao: "",
                        nomeUsina: "",
                        nomeConcessionaria: "",
                        referenciaRebalance: "",
                    })
                }
            />
        </Container>
    );
};

export default ParametrosRebate;
